import AdminLayout from "@/layouts/AdminLayout";
import { useApi } from "@/hooks/useApi";
import { useNotifications } from "@/context/NotificationContext";
import InvoiceModal from "./components/InvoiceModal";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { useState } from "react";
import api from "@/services/api";

interface Invoice {
  id: string;
  number: string;
  description: string | null;
  amount: number;
  status: string;
  due_date: string | null;
  paid_at: string | null;
}

export default function AdminInvoices() {
  const { showToast } = useNotifications();
  const { data: invoices, loading } = useApi<Invoice[]>("/invoices");

  const statusColors: Record<string, string> = {
    paid: "text-cyber-green border-cyber-green/30 bg-cyber-green/10",
    sent: "text-cyber-orange border-cyber-orange/30 bg-cyber-orange/10",
    overdue: "text-cyber-red border-cyber-red/30 bg-cyber-red/10",
    draft: "text-silver-dim border-dark-border bg-dark-border/30",
  };

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [modalInitial, setModalInitial] = useState<any>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const totalRevenue = invoices?.filter((i) => i.status === "paid").reduce((s, i) => s + i.amount, 0) || 0;
  const totalPending = invoices?.filter((i) => i.status === "sent").reduce((s, i) => s + i.amount, 0) || 0;

  const handleSubmitInvoice = async (data: any) => {
    if (modalMode === "create") {
      await api.post("/invoices", data);
      showToast("success", "Invoice created successfully!");
    } else {
      await api.patch(`/invoices/${modalInitial.id}`, data);
      showToast("success", "Invoice updated successfully!");
    }
    window.location.reload();
  };

  const openEdit = async (invoice: Invoice) => {
    try {
      setModalMode("edit");
      const res = await api.get(`/invoices/${invoice.id}`);
      const invoiceData = res.data.data;
      setModalInitial({
        id: invoice.id,
        client_id: invoiceData.client_id,
        number: invoiceData.number,
        description: invoiceData.description,
        due_date: invoiceData.due_date?.split('T')[0],
        items: invoiceData.items || [],
      });
      setShowModal(true);
    } catch (error) {
      showToast("error", "Failed to load invoice details");
      setModalMode("create");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/invoices/${deleteId}`);
      showToast("success", "Invoice deleted successfully!");
      setDeleteId(null);
      window.location.reload();
    } catch (error) {
      showToast("error", "Failed to delete invoice");
    }
  };

  const downloadInvoice = async (invoice: Invoice) => {
    try {
      showToast("info", `Downloading invoice ${invoice.number}...`);
      const response = await api.get(`/invoices/${invoice.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoice.number}.html`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showToast("success", "Invoice downloaded successfully!");
    } catch (error) {
      showToast("error", "Failed to download invoice");
    }
  };

  const sendInvoice = async (invoice: Invoice) => {
    try {
      showToast("info", `Sending invoice ${invoice.number}...`);
      await api.post(`/invoices/${invoice.id}/send`);
      showToast("success", "Invoice sent successfully!");
      // Refresh the list to update status
      window.location.reload();
    } catch (error) {
      showToast("error", "Failed to send invoice");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Invoices</h1>
            <p className="text-silver text-sm mt-1">{invoices?.length || 0} total invoices</p>
          </div>
           <button onClick={() => { setModalMode("create"); setShowModal(true); }}
            className="flex items-center gap-2 bg-electric-blue text-deep-black font-semibold px-4 py-2.5 rounded-xl text-sm hover:shadow-glow-blue transition-all cursor-pointer whitespace-nowrap">
            <i className="ri-add-line" /> Generate Invoice
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
              { label: "Total Collected", value: `${totalRevenue.toLocaleString()} FCFA`, color: "text-cyber-green" },
              { label: "Pending", value: `${totalPending.toLocaleString()} FCFA`, color: "text-cyber-orange" },
            { label: "Total Invoices", value: String(invoices?.length || 0), color: "text-electric-blue" },
            { label: "Paid", value: String(invoices?.filter((i) => i.status === "paid").length || 0), color: "text-cyber-green" },
          ].map((s) => (
            <div key={s.label} className="bg-dark-card border border-dark-border rounded-xl p-4">
              <div className={`text-2xl font-black mb-1 ${s.color}`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{s.value}</div>
              <div className="text-silver text-xs">{s.label}</div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-dark-border">
                    <th className="text-left text-silver-dim font-medium py-3 px-5">Invoice</th>
                    <th className="text-left text-silver-dim font-medium py-3 px-4 hidden md:table-cell">Description</th>
                    <th className="text-left text-silver-dim font-medium py-3 px-4">Amount</th>
                    <th className="text-left text-silver-dim font-medium py-3 px-4">Status</th>
                    <th className="text-left text-silver-dim font-medium py-3 px-4 hidden sm:table-cell">Due Date</th>
                    <th className="text-left text-silver-dim font-medium py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices?.map((inv) => (
                    <tr key={inv.id} className="border-b border-dark-border/50 hover:bg-dark-panel/50 transition-colors">
                      <td className="py-4 px-5">
                        <p className="text-white font-semibold">{inv.number}</p>
                      </td>
                      <td className="py-4 px-4 hidden md:table-cell text-silver-dim text-xs">{inv.description || "—"}</td>
                      <td className="py-4 px-4">
                         <span className="text-white font-bold">{inv.amount.toLocaleString()} FCFA</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`text-xs px-2.5 py-1 rounded-full border ${statusColors[inv.status]}`}>{inv.status}</span>
                      </td>
                      <td className="py-4 px-4 hidden sm:table-cell text-silver-dim text-xs">
                        {inv.due_date ? new Date(inv.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                      </td>
                       <td className="py-4 px-4">
                         <div className="flex items-center gap-2">
                           <button onClick={() => downloadInvoice(inv)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg border border-dark-border text-silver-dim hover:text-white hover:border-electric-blue/40 transition-all cursor-pointer"
                            title="Download Invoice">
                            <i className="ri-download-line text-xs" />
                          </button>
                           <button onClick={() => sendInvoice(inv)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg border border-dark-border text-silver-dim hover:text-electric-blue hover:border-electric-blue/40 transition-all cursor-pointer"
                            title="Send Invoice">
                            <i className="ri-send-plane-line text-xs" />
                          </button>
                           <button onClick={() => openEdit(inv)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg border border-dark-border text-silver-dim hover:text-white hover:border-electric-blue/40 transition-all cursor-pointer"
                            title="Edit Invoice">
                            <i className="ri-edit-line text-xs" />
                          </button>
                           <button onClick={() => setDeleteId(inv.id)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg border border-cyber-red/30 text-cyber-red hover:bg-cyber-red/10 transition-all cursor-pointer"
                            title="Delete Invoice">
                            <i className="ri-delete-bin-line text-xs" />
                          </button>
                        </div>
                       </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <InvoiceModal
          open={showModal}
          mode={modalMode}
          initial={modalInitial}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmitInvoice}
        />

        <ConfirmDialog
          open={!!deleteId}
          title="Delete Invoice"
          message="Are you sure you want to delete this invoice? This action cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setDeleteId(null)}
        />
      </div>
    </AdminLayout>
  );
}

